import { useNavigate } from 'react-router-dom';
import Filmstrip from '../../canvas/Filmstrip.tsx';
import BuildsIndicator from '../../components/BuildsIndicator.tsx';
import PreviewFrame from '../../components/PreviewFrame.tsx';
import CommandBar from '../../canvas/CommandBar.tsx';
import ThemeToggle from '../../components/ThemeToggle.tsx';
import { ChevronLeft } from '../../lib/icons.tsx';
import type { DragEvent, ReactNode } from 'react';
import type { CommandBarProps } from '../../canvas/CommandBar.tsx';
import type { DeckSummaryPayload } from '../../../shared/api.ts';

/** Build shows the live preview; Plan replaces it with the studio plan, so
 *  the plan stays readable while the agent keeps building rather than
 *  flooding the conversation. */
type ChromeTab = 'build' | 'plan';

type NewDeckChromeProps = {
  deckName?: string | null;
  generating?: boolean;
  /** Free prose beside "building…" — a slide count, a phase. */
  buildMeta?: string | null;
  /** Absent until the deck exists on disk; the stage shows `blank` until it
   *  does, so this is the switch between the two. */
  summary?: DeckSummaryPayload | null;
  slug?: string | null;
  /** What the empty stage shows before there is a deck to preview. */
  blank?: ReactNode;
  chat: CommandBarProps['chat'];
  commandBarPlaceholder?: string;
  commandBarDisabled?: boolean;
  renderExtra?: ReactNode;
  convOpen: boolean;
  onToggleConv: (open: boolean) => void;
  onDrop?: (e: DragEvent<HTMLDivElement>) => void;
  /** The hidden file input, rendered by the flow that owns it. */
  fileInput?: ReactNode;
  tab?: ChromeTab;
  onTab?: (tab: ChromeTab) => void;
  /** Present only once the agent flow has a plan — that presence is what
   *  grows the Build/Plan switcher. */
  planPanel?: ReactNode;
  /** Which slide the stage is showing. `-1` selects nothing, which is what a
   *  flow with no slides yet wants. */
  activeIdx?: number;
  /** Pick a slide to look at. Omit and the filmstrip says so rather than
   *  swallowing the click: an enabled-looking tile that does nothing is the
   *  worst of the three states (Alex, 2026-08-18). */
  onSelect?: ((idx: number) => void) | null;
};

/**
 * The presentational scaffold shared by both intake flows: topbar with deck
 * name + build meta, the Filmstrip/PreviewFrame stage, and the CommandBar.
 * Holds no flow state — each child computes its props and the hidden file
 * input, then renders into this chrome.
 *
 * When `planPanel` is provided (agent flow, once the studio plan exists) the
 * topbar grows a Build/Plan tab switcher and the Plan tab replaces the
 * preview area — the plan stays readable while the agent keeps building,
 * without flooding the conversation.
 */
export default function NewDeckChrome({
  deckName,
  generating,
  buildMeta,
  summary,
  slug,
  blank,
  chat,
  commandBarPlaceholder,
  commandBarDisabled,
  renderExtra,
  convOpen,
  onToggleConv,
  onDrop,
  fileInput,
  tab = 'build',
  onTab,
  planPanel,
  activeIdx = -1,
  onSelect,
}: NewDeckChromeProps) {
  const navigate = useNavigate();
  return (
    <div className="editor editor--new" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      <header className="editor__topbar">
        <div className="editor__topbar-left">
          <button
            type="button"
            className="iconbtn"
            onClick={() => navigate('/')}
            title="Back to decks"
            aria-label="Back to decks"
          >
            <ChevronLeft />
          </button>
          <span className="editor__deck-name">{deckName || 'New deck'}</span>
          {generating && (
            <span className="editor__deck-meta muted small">building…{buildMeta ? ` ${buildMeta}` : ''}</span>
          )}
          {planPanel && (
            <nav className="editor__tabs" aria-label="Build views">
              <button
                type="button"
                className={tab === 'build' ? 'editor__tab editor__tab--active' : 'editor__tab'}
                onClick={() => onTab?.('build')}
              >
                Build
              </button>
              <button
                type="button"
                className={tab === 'plan' ? 'editor__tab editor__tab--active' : 'editor__tab'}
                onClick={() => onTab?.('plan')}
              >
                Plan
              </button>
            </nav>
          )}
        </div>
        <div className="editor__topbar-actions">
          <BuildsIndicator />
          <ThemeToggle />
        </div>
      </header>

      <div className="editor__stage">
        <Filmstrip
          sections={summary?.sections || []}
          slides={summary?.slides || []}
          activeIdx={activeIdx}
          onSelect={onSelect || undefined}
          generating={generating}
        />
        {tab === 'plan' && planPanel ? (
          <main className="preview-frame plan-panel">
            <div className="plan-panel__inner">{planPanel}</div>
          </main>
        ) : slug && summary ? (
          /* hmr: a build rewrites slides this frame never asks to reload — its
             only reload signal is the slide COUNT — so Vite's own reload is
             what keeps it current. It also has no version to compare against,
             which is what makes a reload here worth watching rather than
             worth hiding. */
          <PreviewFrame
            slug={slug}
            activeIdx={Math.max(0, activeIdx)}
            onActiveChange={onSelect || undefined}
            reloadKey={summary?.slides?.length || 0}
            hmr
          />
        ) : (
          <main className="preview-frame preview-frame--empty">
            <div className="editor__blank">
              <span className="editor__blank-mark" aria-hidden="true" />
              {blank}
            </div>
          </main>
        )}
        {/* No onJumpToSlide and no onApplied: the intake has no editor to
            navigate to, and its cards are the build's own rather than
            proposals to apply. */}
        <CommandBar
          scope={{ kind: 'new', slug: slug || '', slide: null, label: 'New deck', placeholder: commandBarPlaceholder }}
          chat={chat}
          open={convOpen}
          onToggleOpen={onToggleConv}
          onClearScope={null}
          slides={summary?.slides}
          empty={null}
          renderExtra={renderExtra}
          disabled={commandBarDisabled}
        />
      </div>

      {fileInput}
    </div>
  );
}
